import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle2, Zap, ArrowRight, Mail, Clock } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import PublicNavbar from '@/components/landing/PublicNavbar';
import PublicFooter from '@/components/landing/PublicFooter';
import { Link } from 'react-router-dom';
import { useI18n } from '@/lib/I18nContext';
import { PlatformLogo } from '@/lib/platformIcons';

function useIntersection(ref, threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

// Disponibles ahora. Meta Ads y Google Ads se muestran como disponibles a
// nivel de producto (la integracion tecnica ya esta lista); la conexion real
// de esas dos queda pendiente de la aprobacion de cada plataforma y la
// incorporacion de la empresa -- no es necesario reflejar ese detalle interno
// en la pagina publica.
const AVAILABLE = [
  { id: 'meta', name: 'Meta Ads', subKey: 'integrations.meta_ads_sub', sub: 'Facebook & Instagram Ads', descKey: 'integrations.meta_ads_desc_short', desc: 'Campañas de Facebook e Instagram en tiempo real.' },
  { id: 'instagram', name: 'Instagram Orgánico', subKey: 'integrations.instagram_sub', sub: 'Perfil y contenido', descKey: 'integrations.instagram_desc_short', desc: 'Métricas orgánicas de tu perfil y contenido.' },
  { id: 'google', name: 'Google Ads', subKey: 'integrations.google_ads_sub', sub: 'Search, Display & YouTube', descKey: 'integrations.google_ads_desc_short', desc: 'Search, Display y YouTube con CPC y conversiones.' },
  { id: 'google_analytics', name: 'Google Analytics', subKey: 'integrations.ga4_sub', sub: 'GA4 · Tráfico y conversiones', descKey: 'integrations.ga4_desc_short', desc: 'Tráfico, sesiones y conversiones de GA4.' },
  { id: 'search_console', name: 'Search Console', subKey: 'integrations.gsc_sub', sub: 'Rendimiento en Google', descKey: 'integrations.gsc_desc_short', desc: 'Clics, impresiones y posiciones en Google.' },
  { id: 'shopify', name: 'Shopify', subKey: 'integrations.shopify_sub', sub: 'Ventas, pedidos y clientes', descKey: 'integrations.shopify_desc_short', desc: 'Ventas, pedidos y clientes de tu tienda.' },
  { id: 'woocommerce', name: 'WooCommerce', subKey: 'integrations.woo_sub', sub: 'Tiendas WordPress', descKey: 'integrations.woo_desc_short', desc: 'Pedidos y productos de tu tienda WordPress.' },
];

const COMING_SOON = [
  { id: 'tiktok', name: 'TikTok Ads', subKey: 'integrations.tiktok_sub', sub: 'Campañas y contenido pagado' },
  { id: 'linkedin', name: 'LinkedIn Ads', subKey: 'integrations.linkedin_sub', sub: 'B2B Advertising' },
  { id: 'pinterest', name: 'Pinterest Ads', subKey: 'integrations.pinterest_sub', sub: 'Promoted Pins' },
  { id: 'x', name: 'X Ads', subKey: 'integrations.x_sub', sub: 'Antes Twitter Ads' },
  { id: 'snapchat', name: 'Snapchat Ads', subKey: 'integrations.snapchat_sub', sub: 'Story Ads & Filters' },
  { id: 'youtube', name: 'YouTube Ads', subKey: 'integrations.youtube_ads_sub', sub: 'Video Campaigns' },
];

function NotifyModal({ platform, onClose }) {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    await base44.entities.IntegrationLead.create({ email, platform });
    setSaving(false);
    setDone(true);
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="rounded-2xl shadow-2xl w-full max-w-sm p-6" style={{ background: '#111827', border: '1px solid #1E2D45' }} onClick={e => e.stopPropagation()}>
        {done ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(34,197,94,0.15)' }}>
              <CheckCircle2 className="w-7 h-7" style={{ color: '#22C55E' }} />
            </div>
            <p className="font-heading font-bold" style={{ color: '#F8FAFC' }}>{t('common.done')}!</p>
            <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>{t('integrations.notify_done')} {platform}.</p>
            <button onClick={onClose} className="mt-4 text-sm font-semibold" style={{ color: '#00D5FF' }}>{t('common.close')}</button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,213,255,0.15)' }}>
                <Mail className="w-5 h-5" style={{ color: '#00D5FF' }} />
              </div>
              <div>
                <p className="font-heading font-semibold text-sm" style={{ color: '#F8FAFC' }}>{t('integrations.notify_title')} {platform}</p>
                <p className="text-xs" style={{ color: '#94A3B8' }}>{t('integrations.notify_desc')}</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                required
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="tu@email.com"
                className="w-full px-4 py-2.5 rounded-xl text-sm"
                style={{ background: '#0d1525', border: '1px solid #1E2D45', color: '#F8FAFC' }}
              />
              <button
                type="submit"
                disabled={saving}
                className="w-full font-bold py-2.5 rounded-xl text-sm disabled:opacity-60"
                style={{ background: '#00D5FF', color: '#070B14' }}
              >
                {saving ? t('common.loading') : t('integrations.notify_me')}
              </button>
            </form>
            <button onClick={onClose} className="w-full text-center mt-2 text-xs" style={{ color: '#475569' }}>{t('common.cancel')}</button>
          </>
        )}
      </div>
    </div>
  );
}

function FloatingPlatformBg() {
  const ICONS = ['🟦', '📸', '🔵', '⬛', '🔷', '🔴', '▶️', '👻'];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
      {ICONS.map((icon, i) => (
        <div
          key={i}
          className="absolute text-3xl opacity-10"
          style={{
            left: `${10 + (i * 11) % 85}%`,
            top: `${15 + (i * 17) % 70}%`,
            animation: `float${i % 3} ${3 + i * 0.5}s ease-in-out ${i * 0.4}s infinite`,
          }}
        >
          {icon}
        </div>
      ))}
      <style>{`
        @keyframes float0 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes float1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        @keyframes float2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
      `}</style>
    </div>
  );
}

export default function Integraciones() {
  const { t } = useI18n();
  const [notifyPlatform, setNotifyPlatform] = useState(null);
  const gridRef = useRef(null);
  const gridVisible = useIntersection(gridRef);

  return (
    <div className="min-h-screen font-body overflow-x-hidden" style={{ background: '#070B14', color: '#F8FAFC' }}>
      <PublicNavbar />
      {notifyPlatform && <NotifyModal platform={notifyPlatform} onClose={() => setNotifyPlatform(null)} />}

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 px-6 text-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #070B14 0%, #0d1525 50%, #111827 100%)' }}
      >
        {/* Glow effects */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(0,213,255,0.12) 0%, transparent 60%)' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 70% 50%, rgba(124,58,237,0.12) 0%, transparent 60%)' }} />
        <FloatingPlatformBg />
        <div className="relative z-10 max-w-3xl mx-auto">
          <div
            className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 border"
            style={{ background: 'rgba(0,213,255,0.1)', borderColor: 'rgba(0,213,255,0.3)', color: '#00D5FF' }}
          >
            <Zap className="w-3.5 h-3.5" /> {t('integrations.badge_platforms')}
          </div>
          <h1 className="font-heading font-bold text-white mb-4" style={{ fontSize: 'clamp(1.8rem, 4.5vw, 2.8rem)', lineHeight: 1.2 }}>
            {t('integrations.title')}
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: '#94A3B8' }}>
            {t('integrations.subtitle')}
          </p>
        </div>
      </section>

      {/* Available */}
      <section className="py-16 sm:py-20 px-6" style={{ background: '#0d1525' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <h2 className="font-heading font-bold text-2xl" style={{ color: '#F8FAFC' }}>{t('integrations.available')}</h2>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />{t('integrations.badge_live')}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {AVAILABLE.map((p) => (
              <div key={p.id} className="rounded-2xl p-4 sm:p-5 transition-all hover:-translate-y-0.5" style={{ background: '#1E293B', border: '1px solid #334155' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: '#0F172A' }}>
                  <PlatformLogo platform={p.id} size={p.id === 'woocommerce' ? 26 : 22} compact={p.id === 'woocommerce'} />
                </div>
                <h3 className="font-heading font-bold text-sm mb-0.5" style={{ color: '#F8FAFC' }}>{p.name}</h3>
                <p className="text-xs mb-3" style={{ color: '#64748B' }}>{t(p.subKey, p.sub)}</p>
                <p className="text-xs leading-relaxed mb-3 line-clamp-2" style={{ color: '#94A3B8' }}>{t(p.descKey, p.desc)}</p>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }}>
                  <CheckCircle2 className="w-3 h-3" /> {t('integrations.connect')}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming soon */}
      <section className="py-16 sm:py-20 px-6" style={{ background: '#070B14' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <h2 className="font-heading font-bold text-2xl" style={{ color: '#F8FAFC' }}>{t('integrations.coming_soon')}</h2>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full" style={{ background: 'rgba(245,159,0,0.15)', color: '#F59F00' }}>
              <Clock className="w-3 h-3" />{t('integrations.coming_soon')}
            </span>
          </div>
          <div ref={gridRef} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {COMING_SOON.map((p, i) => (
              <div
                key={p.id}
                className="rounded-2xl p-4 sm:p-5 transition-all hover:-translate-y-0.5"
                style={{
                  background: '#1E293B',
                  border: '1px solid #334155',
                  transition: `opacity 0.5s ease ${i * 80}ms, transform 0.5s ease ${i * 80}ms, box-shadow 0.2s`,
                  opacity: gridVisible ? 1 : 0,
                  transform: gridVisible ? 'translateY(0)' : 'translateY(20px)',
                }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: '#0F172A' }}>
                  <PlatformLogo platform={p.id} size={20} compact={p.id === 'linkedin'} />
                </div>
                <h3 className="font-heading font-bold text-sm mb-0.5" style={{ color: '#F8FAFC' }}>{p.name}</h3>
                <p className="text-xs mb-4" style={{ color: '#64748B' }}>{t(p.subKey, p.sub)}</p>
                <button
                  onClick={() => setNotifyPlatform(p.name)}
                  className="w-full text-center text-xs font-bold py-2 rounded-xl transition-all"
                  style={{ border: '1px solid rgba(0,213,255,0.3)', color: '#00D5FF', background: 'rgba(0,213,255,0.08)' }}
                  onMouseEnter={e => { e.target.style.background = 'rgba(0,213,255,0.15)'; e.target.style.borderColor = '#00D5FF'; }}
                  onMouseLeave={e => { e.target.style.background = 'rgba(0,213,255,0.08)'; e.target.style.borderColor = 'rgba(0,213,255,0.3)'; }}
                >
                  {t('integrations.notify_me')}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open API CTA */}
      <section className="py-16 px-6" style={{ background: '#0d1525' }}>
        <div className="max-w-3xl mx-auto">
          <div className="rounded-3xl p-8 text-center" style={{ background: 'linear-gradient(135deg, #111827, #1a2235)', border: '1px solid #1E2D45' }}>
            <div className="w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center text-2xl" style={{ background: 'rgba(0,213,255,0.1)' }}>🔌</div>
            <h2 className="font-heading font-bold text-white text-2xl mb-3">{t('integrations.open_api_title')}</h2>
            <p className="mb-6 leading-relaxed" style={{ color: '#94A3B8' }}>
              {t('integrations.open_api_desc')}
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl transition-all hover:opacity-90"
              style={{ background: '#00D5FF', color: '#070B14' }}
            >
              {t('integrations.contact_team')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}